import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiService } from "../services/api";
import AdminTabs from "../components/AdminTabs";
import GemaSubmissionWizard from "../components/GemaSubmissionWizard";
import GemaSetlistDialog from "../components/GemaSetlistDialog";
import { toast } from "sonner";

const formatDate = (value) => {
    if (!value) {
        return "-";
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    }).format(parsed);
};

const formatDateTime = (value) => {
    if (!value) {
        return "-";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    }).format(parsed);
};

const formatTime = (value) => {
    if (!value) {
        return "-";
    }
    const raw = String(value).trim();
    if (!raw) {
        return "-";
    }
    const timeMatch = raw.match(/(\d{2}:\d{2})/);
    if (timeMatch?.[1]) {
        return timeMatch[1];
    }
    return raw;
};

const formatEuro = (value) => {
    const amount = Number(value);
    if (Number.isNaN(amount)) {
        return "-";
    }
    return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
    }).format(amount);
};

const GEMA_TYPE_LABELS = {
    TYPE_CONCERT: "Konzert",
    TYPE_LIVE_ENT: "Live-Hintergrund",
    TYPE_STREET: "Straßenfest",
    TYPE_PARTY: "Party / DJ",
    TYPE_SPORT: "Sport / Yoga",
    TYPE_BG: "Hintergrundmusik",
};
const NO_GEMA_MARKER = "[NO_GEMA]";
// Texte wie im GEMA-Portal ("Meine Veranstaltungen"); das Backend liefert sie zusätzlich als gema_setlist_label.
const GEMA_SETLIST_STATUS_LABELS = {
    "0": "Setlist einreichen",
    "1": "vorhanden",
    "2": "nicht nötig",
    "3": "vollständig",
    "4": "nicht vollständig",
};

const isExternallyDone = (row) => {
    const status = String(row?.external?.status || "").trim().toUpperCase();
    const setlistStatus = String(row?.external?.setlist_status || "").trim();
    return Boolean(row?.external_done) || (status === "FAKTURIERT" && !["0", "4"].includes(setlistStatus));
};

const formatSchedule = (date, start, end) => {
    const parts = [formatDate(date)];
    if (start) {
        parts.push(`${formatTime(start)}${end ? `–${formatTime(end)}` : ""}`);
    }
    return parts.join(" ");
};

const GemaRegistrationAlerts = ({ row, busy, onCancellationDone, onAcknowledge }) => {
    const gema = row?.gema || {};
    const cancel = gema.cancel || {};
    const lizenzLabel = row?.external?.lizenz_id
        ? `Lizenz ${row.external.lizenz_id}${row.external.position ? ` / Pos. ${row.external.position}` : ""}`
        : null;
    const portalLink = (
        <a
            href={gema.portal_url || "https://portal.gema.de/app/einzellizenzen"}
            target="_blank"
            rel="noreferrer"
            className="font-semibold underline hover:no-underline"
        >
            GEMA-Portal öffnen{lizenzLabel ? ` (${lizenzLabel})` : ""}
        </a>
    );

    return (
        <div className="mt-2 space-y-2">
            {gema.check_pending && (
                <div className="rounded-md border border-gray-300 bg-gray-50 p-2 text-xs text-gray-700">
                    <p className="font-semibold">Gelöscht – GEMA-Prüfung läuft noch.</p>
                    <p>
                        Ist die Veranstaltung bei der GEMA angemeldet, bleibt sie hier als abgesagt stehen;
                        sonst wird sie endgültig gelöscht.
                        {gema.delete_check?.state === "retry" && ` GEMA zuletzt nicht erreichbar (Versuch ${gema.delete_check.attempts}), neuer Versuch folgt automatisch.`}
                    </p>
                </div>
            )}
            {gema.cancelled_gema_active && (
                <div className="rounded-md border border-red-300 bg-red-100 p-2 text-xs text-red-800">
                    <p className="font-semibold">
                        {gema.occurrence_removed
                            ? "Termin wurde im KuBu-Tool entfernt, ist aber bei der GEMA angemeldet."
                            : "Veranstaltung wurde abgesagt, ist aber bei der GEMA angemeldet."}
                    </p>
                    <p>Bitte im GEMA-Portal über „Änderung beantragen“ stornieren.</p>
                    <p className="mt-1">Meine Veranstaltungen → Optionen → „Änderung beantragen“: {portalLink}</p>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => onCancellationDone(row, true)}
                        className="mt-2 rounded-lg border border-red-400 bg-white px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
                    >
                        {busy ? "Speichern..." : "Bei GEMA storniert"}
                    </button>
                </div>
            )}
            {row?.event_cancelled && cancel.state === "done" && (
                <div className="rounded-md border border-green-200 bg-green-50 p-2 text-xs text-green-800">
                    <p>
                        Bei GEMA storniert am {formatDateTime(cancel.done_at)}
                        {cancel.done_by ? ` von ${cancel.done_by}` : ""}
                        {cancel.source === "gema_abgleich" ? " (automatisch erkannt)" : ""}
                    </p>
                    {cancel.source !== "gema_abgleich" && (
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => onCancellationDone(row, false)}
                            className="mt-1 text-xs font-semibold text-green-800 underline hover:no-underline disabled:opacity-60"
                        >
                            Rückgängig
                        </button>
                    )}
                </div>
            )}
            {gema.storniert_but_active && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                    <p className="font-semibold">Bei der GEMA storniert, im KuBu-Tool aber noch aktiv.</p>
                    <p>Bitte klären, ob die Veranstaltung stattfindet und ggf. neu anmelden. {portalLink}</p>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => onAcknowledge(row, "storniert")}
                        className="mt-2 rounded-lg border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60"
                    >
                        {busy ? "Speichern..." : "Geklärt"}
                    </button>
                </div>
            )}
            {gema.date_change_pending && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                    <p className="font-semibold">Datum/Uhrzeit nach der GEMA-Meldung geändert.</p>
                    <p>
                        Gemeldet: {formatSchedule(gema.registered_date, gema.registered_time, gema.registered_end_time)}
                        {" "}→ jetzt: {formatSchedule(row.event_date, row.event_time, row.event_end_time)}
                    </p>
                    <p>Änderung auch bei der GEMA beantragen. {portalLink}</p>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => onAcknowledge(row, "date_change")}
                        className="mt-2 rounded-lg border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-60"
                    >
                        {busy ? "Speichern..." : "Änderung bei GEMA beantragt"}
                    </button>
                </div>
            )}
        </div>
    );
};

const parseDateOnly = (value) => {
    if (!value) {
        return null;
    }
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    return parsed;
};

const getTodayStart = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const GemaManagement = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [dashboard, setDashboard] = useState({
        kpis: {},
        comparison: [],
        external_errors: {},
    });
    const [filterMode, setFilterMode] = useState("all");
    const [statusUpdatingId, setStatusUpdatingId] = useState(null);
    const [reminderSendingId, setReminderSendingId] = useState(null);
    const [dispatching, setDispatching] = useState(false);
    const [exportingId, setExportingId] = useState(null);
    const [clearingSetlistId, setClearingSetlistId] = useState(null);
    const [timeframe, setTimeframe] = useState("past");
    const [searchTerm, setSearchTerm] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [submissionWizardOpen, setSubmissionWizardOpen] = useState(false);
    const [selectedSubmissionEvent, setSelectedSubmissionEvent] = useState(null);
    const [registrationUpdatingId, setRegistrationUpdatingId] = useState(null);
    const [setlistDialogRow, setSetlistDialogRow] = useState(null);

    const fetchDashboard = async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }
            const data = await apiService.getGemaDashboard();
            setDashboard({
                kpis: data?.kpis || {},
                comparison: Array.isArray(data?.comparison) ? data.comparison : [],
                external_errors: data?.external_errors || {},
            });
        } catch (error) {
            console.error("Error loading GEMA dashboard:", error);
            toast.error(error.response?.data?.detail || "GEMA Dashboard konnte nicht geladen werden");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchDashboard(false);
    }, []);

    const rows = useMemo(() => {
        const today = getTodayStart();
        const search = String(searchTerm || "").trim().toLowerCase();
        const fromDate = parseDateOnly(dateFrom);
        const toDate = parseDateOnly(dateTo);
        const effectiveFrom = fromDate && toDate && fromDate > toDate ? toDate : fromDate;
        const effectiveTo = fromDate && toDate && fromDate > toDate ? fromDate : toDate;

        let filteredRows = dashboard.comparison;
        if (filterMode === "action") {
            filteredRows = dashboard.comparison.filter((row) => row.action_required);
        }
        if (filterMode === "cancelled_gema") {
            filteredRows = dashboard.comparison.filter((row) => row?.gema?.cancelled_gema_active);
        }
        if (filterMode === "open") {
            filteredRows = dashboard.comparison.filter(
                (row) => !row.manually_done && !isExternallyDone(row) && row.event_setlist_status !== "Eingereicht" && !row.gema_self_managed && !row.gema_frei
            );
        }
        if (filterMode === "done") {
            filteredRows = dashboard.comparison.filter(
                (row) => row.manually_done || isExternallyDone(row) || row.event_setlist_status === "Eingereicht" || row.gema_self_managed || row.gema_frei
            );
        }

        // Handlungsbedarf soll unabhängig vom Zeitraum vollständig sichtbar sein (wie die Kachel).
        const ignoresTimeframe = filterMode === "action" || filterMode === "cancelled_gema";
        if (timeframe !== "all" && !ignoresTimeframe) {
            filteredRows = filteredRows.filter((row) => {
                const eventDate = parseDateOnly(row.event_date);
                if (!eventDate) {
                    return false;
                }
                if (timeframe === "past") {
                    return eventDate < today;
                }
                if (timeframe === "upcoming") {
                    return eventDate >= today;
                }
                return true;
            });
        }

        if (effectiveFrom || effectiveTo) {
            filteredRows = filteredRows.filter((row) => {
                const eventDate = parseDateOnly(row.event_date);
                if (!eventDate) {
                    return false;
                }
                if (effectiveFrom && eventDate < effectiveFrom) {
                    return false;
                }
                if (effectiveTo && eventDate > effectiveTo) {
                    return false;
                }
                return true;
            });
        }

        if (search) {
            filteredRows = filteredRows.filter((row) => {
                const haystack = [
                    row?.event_title,
                    row?.organizer_name,
                    row?.organizer_email,
                    row?.external?.event_bezeichnung,
                ]
                    .map((value) => String(value || "").toLowerCase())
                    .join(" ");
                return haystack.includes(search);
            });
        }

        if (timeframe === "upcoming") {
            filteredRows = [...filteredRows].sort((a, b) => {
                const dateA = parseDateOnly(a?.event_date);
                const dateB = parseDateOnly(b?.event_date);
                if (!dateA && !dateB) {
                    return String(a?.event_title || "").localeCompare(String(b?.event_title || ""));
                }
                if (!dateA) {
                    return 1;
                }
                if (!dateB) {
                    return -1;
                }
                if (dateA.getTime() !== dateB.getTime()) {
                    return dateA.getTime() - dateB.getTime();
                }
                const timeA = String(a?.event_time || "").trim();
                const timeB = String(b?.event_time || "").trim();
                if (timeA !== timeB) {
                    return timeA.localeCompare(timeB);
                }
                return String(a?.event_title || "").localeCompare(String(b?.event_title || ""));
            });
        }

        return filteredRows;
    }, [dashboard.comparison, filterMode, timeframe, dateFrom, dateTo, searchTerm]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(rows.length / rowsPerPage)), [rows.length, rowsPerPage]);

    useEffect(() => {
        setPage(1);
    }, [filterMode, timeframe, dateFrom, dateTo, searchTerm, rowsPerPage]);

    useEffect(() => {
        setPage((current) => Math.min(current, totalPages));
    }, [totalPages]);

    const paginatedRows = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return rows.slice(start, start + rowsPerPage);
    }, [rows, page, rowsPerPage]);

    const paginationWindow = useMemo(() => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, index) => index + 1);
        }
        const pages = new Set([1, totalPages, page - 1, page, page + 1]);
        return [...pages].filter((value) => value >= 1 && value <= totalPages).sort((a, b) => a - b);
    }, [page, totalPages]);

    const handleManualDoneToggle = async (row) => {
        try {
            setStatusUpdatingId(row.event_id);
            await apiService.updateGemaManualStatus(row.event_id, !row.manually_done, row.manual_note || "");
            toast.success(!row.manually_done ? "Als erledigt markiert" : "Erledigt-Markierung entfernt");
            await fetchDashboard(true);
        } catch (error) {
            console.error("Error updating manual GEMA status:", error);
            toast.error(error.response?.data?.detail || "Status konnte nicht aktualisiert werden");
        } finally {
            setStatusUpdatingId(null);
        }
    };

    const hasNoGemaMarker = (note) => String(note || "").includes(NO_GEMA_MARKER);

    const buildNoGemaNote = (note) => {
        const trimmed = String(note || "").trim();
        if (!trimmed) {
            return `${NO_GEMA_MARKER} Keine GEMA erforderlich`;
        }
        if (hasNoGemaMarker(trimmed)) {
            return trimmed;
        }
        return `${trimmed}\n${NO_GEMA_MARKER} Keine GEMA erforderlich`;
    };

    const removeNoGemaMarker = (note) => {
        const lines = String(note || "")
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line && !line.includes(NO_GEMA_MARKER));
        return lines.join("\n");
    };

    const handleNoGemaToggle = async (row) => {
        try {
            setStatusUpdatingId(row.event_id);
            const isNoGema = hasNoGemaMarker(row.manual_note);
            if (isNoGema) {
                const cleaned = removeNoGemaMarker(row.manual_note);
                const shouldStayDone = Boolean(cleaned);
                await apiService.updateGemaManualStatus(row.event_id, shouldStayDone, cleaned);
                toast.success("Keine-GEMA-Markierung entfernt");
            } else {
                await apiService.updateGemaManualStatus(row.event_id, true, buildNoGemaNote(row.manual_note));
                toast.success("Als 'Keine GEMA' markiert");
            }
            await fetchDashboard(true);
        } catch (error) {
            console.error("Error updating no-gema status:", error);
            toast.error(error.response?.data?.detail || "Keine-GEMA-Status konnte nicht aktualisiert werden");
        } finally {
            setStatusUpdatingId(null);
        }
    };

    const handleSendReminder = async (row) => {
        const message = window.prompt("Optionaler Hinweis an den Veranstalter:", "");
        if (message === null) {
            return;
        }
        try {
            setReminderSendingId(row.event_id);
            const result = await apiService.sendGemaReminder(row.event_id, message);
            toast.success(`Reminder versendet an ${result.recipient}`);
            await fetchDashboard(true);
        } catch (error) {
            console.error("Error sending GEMA reminder:", error);
            toast.error(error.response?.data?.detail || "Reminder konnte nicht gesendet werden");
        } finally {
            setReminderSendingId(null);
        }
    };

    const handleDispatchReminders = async () => {
        try {
            setDispatching(true);
            const result = await apiService.dispatchGemaReminders();
            toast.success(
                `Reminder geprüft: ${result.events_checked || 0} Events, ${result.emails_sent || 0} Mails versendet`
            );
            await fetchDashboard(true);
        } catch (error) {
            console.error("Error dispatching GEMA reminders:", error);
            toast.error(error.response?.data?.detail || "Reminder-Dispatch fehlgeschlagen");
        } finally {
            setDispatching(false);
        }
    };

    const handleExportSetlist = async (row) => {
        try {
            setExportingId(row.event_id);
            const response = await apiService.exportGemaSetlistXlsx(row.event_id, false);
            const blob = new Blob(
                [response.data],
                { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
            );
            const contentDisposition = response.headers?.["content-disposition"] || "";
            const filenameMatch = contentDisposition.match(/filename=\"([^\"]+)\"/i);
            const filename = filenameMatch?.[1] || `gema_setlist_${row.event_id}.xlsx`;
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(downloadUrl);
            toast.success("Setlist-Export erstellt");
        } catch (error) {
            console.error("Error exporting setlist:", error);
            toast.error(error.response?.data?.detail || "Setlist-Export fehlgeschlagen");
        } finally {
            setExportingId(null);
        }
    };

    const handleClearSetlist = async (row) => {
        const shouldClear = window.confirm(`Setlist für "${row.event_title || "diese Veranstaltung"}" wirklich löschen?`);
        if (!shouldClear) {
            return;
        }
        try {
            setClearingSetlistId(row.event_id);
            const result = await apiService.clearEventSetlist(row.event_id);
            toast.success(`Setlist gelöscht (Status: ${result?.setlist_status || "Ausstehend"})`);
            await fetchDashboard(true);
        } catch (error) {
            console.error("Error clearing setlist:", error);
            toast.error(error.response?.data?.detail || "Setlist konnte nicht gelöscht werden");
        } finally {
            setClearingSetlistId(null);
        }
    };

    const handleCancellationDone = async (row, done) => {
        if (done && !window.confirm(`Wurde „${row.event_title || "die Veranstaltung"}“ im GEMA-Portal storniert?`)) {
            return;
        }
        try {
            setRegistrationUpdatingId(row.row_id);
            await apiService.markGemaCancellationDone(row.event_id, row?.gema?.registration_key, done);
            toast.success(done ? "Als bei GEMA storniert markiert" : "Stornierungs-Markierung entfernt");
            await fetchDashboard(true);
        } catch (error) {
            console.error("Error updating GEMA cancellation:", error);
            toast.error(error.response?.data?.detail || "Status konnte nicht gespeichert werden");
        } finally {
            setRegistrationUpdatingId(null);
        }
    };

    const handleAcknowledgeRegistration = async (row, kind) => {
        try {
            setRegistrationUpdatingId(row.row_id);
            await apiService.acknowledgeGemaRegistrationHint(
                row.event_id,
                row?.gema?.registration_key,
                kind,
                row.occurrence_id,
            );
            toast.success(kind === "date_change" ? "Terminänderung als beantragt vermerkt" : "Als geklärt markiert");
            await fetchDashboard(true);
        } catch (error) {
            console.error("Error acknowledging GEMA hint:", error);
            toast.error(error.response?.data?.detail || "Status konnte nicht gespeichert werden");
        } finally {
            setRegistrationUpdatingId(null);
        }
    };

    const openSubmissionWizard = (row) => {
        setSelectedSubmissionEvent(row);
        setSubmissionWizardOpen(true);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
            </div>
        );
    }

    const kpis = dashboard.kpis || {};

    return (
        <div className="space-y-6">
            <div className="rounded-xl bg-gradient-to-r from-red-700 via-rose-700 to-orange-600 px-6 py-6 text-white shadow-lg">
                <h2 className="text-3xl font-bold">GEMA Management</h2>
                <p className="text-red-100">GEMA-Admin Steuerung - Don't touch if you are not Benji</p>
            </div>

            <AdminTabs />

            {!!dashboard.external_errors?.lizenzen && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Hinweis: {dashboard.external_errors.lizenzen}
                </div>
            )}

            <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                    <p className="text-xs text-gray-500">Aktuelle Events</p>
                    <p className="text-2xl font-bold text-gray-900">{kpis.current_events || 0}</p>
                </div>
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                    <p className="text-xs text-blue-700">Mit GEMA gematcht</p>
                    <p className="text-2xl font-bold text-blue-900">{kpis.matched_with_gema || 0}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs text-gray-600">Fehlen in GEMA</p>
                    <p className="text-2xl font-bold text-gray-900">{kpis.missing_in_gema || 0}</p>
                </div>
                <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                    <p className="text-xs text-red-700">Aktion erforderlich</p>
                    <p className="text-2xl font-bold text-red-700">{kpis.action_required || 0}</p>
                    {(kpis.cancelled_gema_active || 0) > 0 && (
                        <p className="text-xs font-semibold text-red-700">davon {kpis.cancelled_gema_active} abgesagt, bei GEMA aktiv</p>
                    )}
                </div>
                <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                    <p className="text-xs text-green-700">Manuell erledigt</p>
                    <p className="text-2xl font-bold text-green-700">{kpis.manually_done || 0}</p>
                </div>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <p className="text-xs text-amber-700">Reminder versendet</p>
                    <p className="text-2xl font-bold text-amber-700">{kpis.reminders_sent || 0}</p>
                </div>
                <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
                    <p className="text-xs text-indigo-700">Eigengema</p>
                    <p className="text-2xl font-bold text-indigo-700">{kpis.gema_self_managed || 0}</p>
                </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setFilterMode("all")}
                        className={`rounded-lg px-3 py-2 text-xs font-semibold border ${filterMode === "all" ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 text-gray-700 bg-white"}`}
                    >
                        Alle
                    </button>
                    <button
                        type="button"
                        onClick={() => setFilterMode("action")}
                        className={`rounded-lg px-3 py-2 text-xs font-semibold border ${filterMode === "action" ? "bg-red-700 text-white border-red-700" : "border-red-200 text-red-700 bg-red-50"}`}
                    >
                        Aktion erforderlich
                    </button>
                    <button
                        type="button"
                        onClick={() => setFilterMode("cancelled_gema")}
                        className={`rounded-lg px-3 py-2 text-xs font-semibold border ${filterMode === "cancelled_gema" ? "bg-red-900 text-white border-red-900" : "border-red-300 text-red-900 bg-red-100"}`}
                    >
                        Abgesagt – bei GEMA aktiv ({kpis.cancelled_gema_active || 0})
                    </button>
                    <button
                        type="button"
                        onClick={() => setFilterMode("open")}
                        className={`rounded-lg px-3 py-2 text-xs font-semibold border ${filterMode === "open" ? "bg-orange-700 text-white border-orange-700" : "border-orange-200 text-orange-700 bg-orange-50"}`}
                    >
                        Offen
                    </button>
                    <button
                        type="button"
                        onClick={() => setFilterMode("done")}
                        className={`rounded-lg px-3 py-2 text-xs font-semibold border ${filterMode === "done" ? "bg-green-700 text-white border-green-700" : "border-green-200 text-green-700 bg-green-50"}`}
                    >
                        Erledigt
                    </button>
                    <button
                        type="button"
                        onClick={() => fetchDashboard(true)}
                        disabled={refreshing}
                        className="ml-auto rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                        {refreshing ? "Aktualisiere..." : "Neu laden"}
                    </button>
                    <button
                        type="button"
                        onClick={handleDispatchReminders}
                        disabled={dispatching}
                        className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                    >
                        {dispatching ? "Prüfe..." : "Reminder jetzt prüfen"}
                    </button>
                </div>
                <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-6">
                    <div className="xl:col-span-2">
                        <label htmlFor="gema-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Suche (Event / Person)
                        </label>
                        <input
                            id="gema-search"
                            type="text"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder="z.B. Veranstaltung, Name oder E-Mail"
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200"
                        />
                    </div>
                    <div className="xl:col-span-2">
                        <label htmlFor="gema-timeframe" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Zeitraum
                        </label>
                        <select
                            id="gema-timeframe"
                            value={timeframe}
                            onChange={(event) => setTimeframe(event.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200"
                        >
                            <option value="past">Vergangene</option>
                            <option value="upcoming">Kommende</option>
                            <option value="all">Alle</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="gema-date-from" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Von
                        </label>
                        <input
                            id="gema-date-from"
                            type="date"
                            value={dateFrom}
                            onChange={(event) => setDateFrom(event.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200"
                        />
                    </div>
                    <div>
                        <label htmlFor="gema-date-to" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Bis
                        </label>
                        <input
                            id="gema-date-to"
                            type="date"
                            value={dateTo}
                            onChange={(event) => setDateTo(event.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200"
                        />
                    </div>
                    <div>
                        <label htmlFor="gema-rows-per-page" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Pro Seite
                        </label>
                        <select
                            id="gema-rows-per-page"
                            value={rowsPerPage}
                            onChange={(event) => setRowsPerPage(Number(event.target.value))}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                    <thead className="border-b bg-gray-50 text-left text-xs uppercase text-gray-600">
                        <tr>
                            <th className="px-4 py-3">Event</th>
                            <th className="px-4 py-3">Abgleich</th>
                            <th className="px-4 py-3">GEMA</th>
                            <th className="px-4 py-3">Setlist</th>
                            <th className="px-4 py-3">Reminder</th>
                            <th className="px-4 py-3">Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.map((row) => (
                            <tr key={row.row_id || `${row.event_id}-${row.event_date || ""}-${row.occurrence_index || 1}`} className={`border-b last:border-none ${row.action_required ? "bg-red-50/60" : ""}`}>
                                <td className="px-4 py-3 align-top">
                                    <p className="font-semibold text-gray-900">{row.event_title || "-"}</p>
                                    <p className="text-xs text-gray-500">{formatDate(row.event_date)}</p>
                                    {(row.occurrence_count || 1) > 1 && (
                                        <p className="text-xs font-semibold text-indigo-700">
                                            Termin {row.occurrence_index || 1} von {row.occurrence_count}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-500">
                                        Start: {formatTime(row.event_time)} | Ende: {formatTime(row.event_end_time)}
                                    </p>
                                    <p className="text-xs text-gray-500">{row.organizer_name || row.organizer_email || "-"}</p>
                                    {row.event_id && (
                                        <Link
                                            to={`/create?eventId=${encodeURIComponent(row.event_id)}&returnTo=admin`}
                                            className="mt-1 inline-flex text-xs font-semibold text-indigo-700 hover:text-indigo-900 hover:underline"
                                        >
                                            Veranstaltung öffnen
                                        </Link>
                                    )}
                                    {row.imported_from_wp && (
                                        <span className="mt-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                                            WP-Import
                                        </span>
                                    )}
                                    {row.event_cancelled && (
                                        <span className="mt-1 ml-1 inline-flex rounded-full bg-red-700 px-2 py-0.5 text-xs font-semibold text-white">
                                            {row.event_deleted ? "Gelöscht" : row?.gema?.occurrence_removed ? "Termin entfernt" : "Abgesagt"}
                                        </span>
                                    )}
                                    <GemaRegistrationAlerts
                                        row={row}
                                        busy={registrationUpdatingId === row.row_id}
                                        onCancellationDone={handleCancellationDone}
                                        onAcknowledge={handleAcknowledgeRegistration}
                                    />
                                </td>
                                <td className="px-4 py-3 align-top">
                                    {row.gema_self_managed ? (
                                        <span className="inline-flex rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">Eigengema</span>
                                    ) : row.gema_frei && !row.matched ? (
                                        <span className="inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">GEMA-frei</span>
                                    ) : row.matched ? (
                                        <div className="space-y-1">
                                            <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">Gematcht</span>
                                            {row?.gema?.match_quality === "date_only" && (
                                                <p className="text-[11px] font-semibold text-amber-700">Zuordnung unsicher (nur über Datum)</p>
                                            )}
                                            {row?.gema?.gema_storniert && (
                                                <p className="text-[11px] font-semibold text-gray-700">GEMA: storniert</p>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="inline-flex rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">Fehlt in GEMA</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 align-top">
                                    <div className="space-y-2">
                                        <details className="rounded-md border border-indigo-100 bg-indigo-50 p-2">
                                            <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
                                                Lokal übermittelt
                                            </summary>
                                            <div className="mt-2 space-y-1">
                                                <p className="text-xs text-gray-700">Typ: {GEMA_TYPE_LABELS[row?.local_gema?.gema_event_type] || row?.local_gema?.gema_event_type || "-"}</p>
                                                <p className="text-xs text-gray-700">GEMA relevant (Veranstalter): {row?.local_gema?.gema_relevant ? "Ja" : "Nein"}</p>
                                                <p className="text-xs text-gray-700">Tarif (Kürzel, intern): {row?.local_gema?.gema_tarif_vorschlag || "-"}</p>
                                                <p className="text-xs text-gray-700">Musikart: {row?.local_gema?.musik_art || "-"}</p>
                                                <p className="text-xs text-gray-700">Netto Eintritt: {formatEuro(row?.local_gema?.eintrittsgeld_netto)}</p>
                                                <p className="text-xs text-gray-700">Räume: {Array.isArray(row?.local_gema?.veranstaltungsraeume) && row.local_gema.veranstaltungsraeume.length > 0 ? row.local_gema.veranstaltungsraeume.join(", ") : "-"}</p>
                                                <p className="text-xs text-gray-700">
                                                    Besucher (effektiv): {row?.local_gema?.besucher_anzahl_effektiv ?? "-"}
                                                    {row?.local_gema?.besucher_anzahl_quelle === "kennzahl" ? " (aus Kennzahl)" : " (geschätzt)"}
                                                </p>
                                                <p className="text-xs text-gray-700">Besucher (geschätzt): {row?.local_gema?.besucher_anzahl_geschaetzt ?? "-"}</p>
                                                <p className="text-xs text-gray-700">Besucher (Kennzahl): {row?.local_gema?.besucher_anzahl_tatsaechlich ?? "-"}</p>
                                                <p className="text-xs text-gray-700">Gesamtumsatz: {formatEuro(row?.local_gema?.total_revenue)}</p>
                                                <p className="text-xs text-gray-700">Eintrittseinnahmen: {formatEuro(row?.local_gema?.admission_revenue)}</p>
                                                <p className="text-xs text-gray-700">Gesamtgage: {formatEuro(row?.local_gema?.dj_costs_total)}</p>
                                                <p className="text-xs text-gray-700">Setlist lokal: {row?.local_gema?.setlist_status || "-"} ({row?.local_gema?.setlist_entries_count ?? 0} Songs)</p>
                                            </div>
                                        </details>
                                        {row.external ? (
                                            <details className="rounded-md border border-gray-200 bg-gray-50 p-2">
                                                <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-gray-700">
                                                    Bei GEMA
                                                </summary>
                                                <div className="mt-2 space-y-1">
                                                    <p className="text-xs text-gray-700">GEMA-Name: {row.external.event_bezeichnung || "-"}</p>
                                                    <p className="text-xs text-gray-700">Lizenz: {row.external.lizenz_id || "-"}</p>
                                                    <p className={`text-xs ${row.gema_status_unknown ? "text-gray-500 italic" : "text-gray-700"}`}>
                                                        Bearbeitungsstatus: {row.gema_status_label || "-"}
                                                        {row.gema_status_unknown && ` (${row.external.status})`}
                                                    </p>
                                                    <p className="text-xs text-gray-700">
                                                        Setlist-Status: {GEMA_SETLIST_STATUS_LABELS[String(row.external.setlist_status || "")] || row.external.setlist_status || "-"}
                                                    </p>
                                                    <p className="text-xs text-gray-700">Datum: {formatDate(row.external.event_datum_beginn)}</p>
                                                    <p className="text-xs text-gray-700">Betrag: {formatEuro(row.external.betrag)}</p>
                                                    <p className="text-xs text-gray-700">Ort: {row.external.nutzungs_ort || "-"}</p>
                                                </div>
                                            </details>
                                        ) : row.gema_self_managed ? (
                                            <span className="inline-flex rounded-full bg-indigo-100 px-2 py-1 text-[11px] font-semibold text-indigo-700">
                                                Eigengema - kein Abgleich notwendig
                                            </span>
                                        ) : row.gema_frei ? (
                                            <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700">
                                                Nur GEMA-freie Musik - keine Anmeldung notwendig
                                            </span>
                                        ) : (
                                            <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-[11px] font-semibold text-red-700">
                                                Fehlt in GEMA
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-3 align-top">
                                    <div className="flex flex-wrap gap-2">
                                        {row.gema_self_managed && (
                                            <span className="inline-flex rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white">Eigengema</span>
                                        )}
                                        {row.setlist_action_required && (
                                            <span className="inline-flex rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white">Setlist fehlt</span>
                                        )}
                                        {row?.gema?.setlist_submission?.submitted_at && (
                                            <span className="inline-flex rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">
                                                Setlist eingereicht {formatDate(row.gema.setlist_submission.submitted_at.slice(0, 10))}
                                            </span>
                                        )}
                                        {row.manually_done && (
                                            <span className="inline-flex rounded-full bg-green-600 px-3 py-1 text-xs font-semibold text-white">Manuell erledigt</span>
                                        )}
                                        {hasNoGemaMarker(row.manual_note) && (
                                            <span className="inline-flex rounded-full bg-slate-700 px-3 py-1 text-xs font-semibold text-white">Keine GEMA</span>
                                        )}
                                        {isExternallyDone(row) && !row.manually_done && (
                                            <span className="inline-flex rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">Erledigt (fakturiert)</span>
                                        )}
                                        {String(row?.external?.setlist_status || "") === "1" && !row.manually_done && !isExternallyDone(row) && (
                                            <span className="inline-flex rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
                                                Setlist vorhanden
                                            </span>
                                        )}
                                        {!row.setlist_action_required
                                            && !row.manually_done
                                            && !isExternallyDone(row)
                                            && String(row?.external?.setlist_status || "") !== "1" && (
                                            <span className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                                                {row.event_setlist_status || "-"}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-3 align-top">
                                    <p className="text-xs text-gray-700">{formatDateTime(row.last_reminder_sent_at)}</p>
                                </td>
                                <td className="px-4 py-3 align-top">
                                    <div className="flex flex-wrap gap-2">
                                        {!row.event_cancelled && !row.matched && !row.gema_self_managed && !row.gema_frei && (
                                            <button
                                                type="button"
                                                onClick={() => openSubmissionWizard(row)}
                                                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                                            >
                                                Zu GEMA übertragen
                                            </button>
                                        )}
                                        {!row.event_cancelled && row.matched && (row.is_setlist_required || String(row?.external?.setlist_status || "") === "0") && (
                                            <button
                                                type="button"
                                                onClick={() => setSetlistDialogRow(row)}
                                                className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                                            >
                                                Setlist an GEMA
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleManualDoneToggle(row)}
                                            disabled={statusUpdatingId === row.event_id}
                                            className="rounded-lg border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:opacity-60"
                                        >
                                            {statusUpdatingId === row.event_id ? "Speichern..." : row.manually_done ? "Erledigt aufheben" : "Als erledigt markieren"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleNoGemaToggle(row)}
                                            disabled={statusUpdatingId === row.event_id}
                                            className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                                        >
                                            {statusUpdatingId === row.event_id
                                                ? "Speichern..."
                                                : hasNoGemaMarker(row.manual_note)
                                                    ? "Keine GEMA aufheben"
                                                    : "Keine GEMA"}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleSendReminder(row)}
                                            disabled={reminderSendingId === row.event_id}
                                            className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60"
                                        >
                                            {reminderSendingId === row.event_id ? "Sende..." : "Reminder senden"}
                                        </button>
                                        {row.is_setlist_required && (row?.local_gema?.setlist_entries_count ?? 0) > 0 && (
                                            <button
                                                type="button"
                                                onClick={() => handleExportSetlist(row)}
                                                disabled={exportingId === row.event_id}
                                                className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                                            >
                                                {exportingId === row.event_id ? "Export..." : "Setlist Export"}
                                            </button>
                                        )}
                                        {row.is_setlist_required && (row?.local_gema?.setlist_entries_count ?? 0) === 0 && (
                                            <span className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-600">
                                                Keine Setlist eingereicht
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleClearSetlist(row)}
                                            disabled={clearingSetlistId === row.event_id}
                                            className="rounded-lg border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                                        >
                                            {clearingSetlistId === row.event_id ? "Lösche..." : "Setlist löschen"}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                                    Keine Einträge für den aktuellen Filter.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {rows.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <p className="text-sm text-gray-600">
                            Zeige {(page - 1) * rowsPerPage + 1}-{Math.min(page * rowsPerPage, rows.length)} von {rows.length}
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPage((current) => Math.max(1, current - 1))}
                                disabled={page <= 1}
                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Zurück
                            </button>
                            {paginationWindow.map((pageNumber) => (
                                <button
                                    key={pageNumber}
                                    type="button"
                                    onClick={() => setPage(pageNumber)}
                                    className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${page === pageNumber ? "border-red-700 bg-red-700 text-white" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}
                                >
                                    {pageNumber}
                                </button>
                            ))}
                            <button
                                type="button"
                                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                                disabled={page >= totalPages}
                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Weiter
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <GemaSubmissionWizard
                open={submissionWizardOpen}
                onOpenChange={setSubmissionWizardOpen}
                selectedEvent={selectedSubmissionEvent}
                onSubmitted={() => fetchDashboard(true)}
            />
            <GemaSetlistDialog
                open={Boolean(setlistDialogRow)}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setSetlistDialogRow(null);
                    }
                }}
                row={setlistDialogRow}
                onSaved={() => fetchDashboard(true)}
            />
        </div>
    );
};

export default GemaManagement;
